import { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Container, Header, Table, Button, Message, Dropdown, Label, Icon, Modal,
} from 'semantic-ui-react';
import { Navigate } from 'react-router-dom';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { PATHS } from '../../../paths';

/**
 * People who signed up without a valid organisation code.
 *
 * **This is the design working, not a fault.** Somebody who presents a code Joyya does not
 * recognise gets an account that reaches nothing — no membership, no organisation, nothing to
 * see. Refusing the signup outright would be worse: it would confirm which codes are real, and
 * anybody could then probe for one. An inert account tells an attacker nothing and costs a
 * genuine applicant only a wait.
 *
 * So this is a **waiting room**, and the screen is where somebody who can recognise a name decides
 * whether they belong. There were 27 people in it on 2026-10-08, spanning two years, because until
 * now the room had no door — the list could only be found by writing SQL.
 */

const ORPHANS = gql`
  query OrphanedAccounts {
    canManageOrphanedAccounts
    orphanedAccounts {
      userId name email signedUpAt signupCode funnelCode canSignIn
    }
    me { organisations { id name } }
  }
`;

const ATTACH = gql`
  mutation AttachOrphanToOrganisation($userId: Int!, $organisationId: Int!) {
    attachOrphanToOrganisation(userId: $userId, organisationId: $organisationId)
  }
`;

const DELETE = gql`
  mutation DeleteOrphanedAccount($userId: Int!) {
    deleteOrphanedAccount(userId: $userId)
  }
`;

const formatDate = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

export const OrphanedAccounts = () => {
  const { data, loading, refetch } = useQuery(ORPHANS, { fetchPolicy: 'network-only' });
  const [attach, { loading: attaching }] = useMutation(ATTACH);
  const [remove, { loading: removing }] = useMutation(DELETE);

  const [chosen, setChosen] = useState<Record<number, number>>({});
  const [confirming, setConfirming] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (loading) return null;

  if (!data?.canManageOrphanedAccounts) {
    return <Navigate to={PATHS.CLASSES} replace />;
  }

  const rows = (data?.orphanedAccounts ?? []) as any[];
  const organisations = (data?.me?.organisations ?? []) as { id: number; name: string }[];

  const act = async (fn: () => Promise<unknown>, failure: string) => {
    setError(null);
    try {
      await fn();
      await refetch();
    } catch (e: any) {
      setError(e?.message ?? failure);
    }
  };

  return (
    <ResponsiveContainer>
      <Container style={{ paddingTop: '2em', maxWidth: '900px' }}>
        <Header as="h1">
          Orphaned accounts
          <Header.Subheader>
            People who signed up without a code Joyya recognised. They have an account that
            reaches nothing until somebody who knows them puts them into an organisation.
          </Header.Subheader>
        </Header>

        {error && <Message negative onDismiss={() => setError(null)}>{error}</Message>}

        {rows.length === 0
          ? <Message positive content="Nobody is waiting. Everybody with a login belongs somewhere." />
          : (
            <Table compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Email</Table.HeaderCell>
                  <Table.HeaderCell>Signed up</Table.HeaderCell>
                  <Table.HeaderCell>Code tried</Table.HeaderCell>
                  <Table.HeaderCell>Add to organisation</Table.HeaderCell>
                  <Table.HeaderCell />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {rows.map((r) => (
                  <Table.Row key={r.userId}>
                    <Table.Cell>
                      {r.email ?? <span style={{ color: '#767676' }}>no address on file</span>}
                      {r.name && <div style={{ color: '#767676', fontSize: '0.9em' }}>{r.name}</div>}
                      {/* An invited account that never set a password looks identical to an
                          orphan otherwise. Saying so stops somebody "fixing" a live invitation. */}
                      {!r.canSignIn && (
                        <Label size="tiny" style={{ marginTop: '0.3em' }}>
                          <Icon name="hourglass half" />
                          No password set — may be a pending invitation
                        </Label>
                      )}
                    </Table.Cell>
                    <Table.Cell>{formatDate(r.signedUpAt)}</Table.Cell>
                    <Table.Cell>
                      {r.signupCode
                        ? <code>{r.signupCode}</code>
                        : r.funnelCode
                          ? (
                            <span>
                              <code>{r.funnelCode}</code>
                              <div style={{ color: '#767676', fontSize: '0.85em' }}>
                                from the install guide, not the signup form
                              </div>
                            </span>
                          )
                          : (
                            <span style={{ color: '#767676' }}>
                              not recorded
                            </span>
                          )}
                    </Table.Cell>
                    <Table.Cell>
                      <Dropdown
                        selection
                        search
                        placeholder="Choose one…"
                        style={{ minWidth: '14em' }}
                        value={chosen[r.userId]}
                        options={organisations.map((o) => ({ key: o.id, text: o.name, value: o.id }))}
                        onChange={(_, d) => setChosen({ ...chosen, [r.userId]: d.value as number })}
                      />
                      <Button
                        size="tiny"
                        primary
                        style={{ marginLeft: '0.5em' }}
                        loading={attaching}
                        disabled={chosen[r.userId] == null || attaching}
                        onClick={() => act(
                          () => attach({ variables: { userId: r.userId, organisationId: chosen[r.userId] } }),
                          'Could not add them to that organisation.',
                        )}
                      >
                        Add
                      </Button>
                    </Table.Cell>
                    <Table.Cell collapsing textAlign="right">
                      <Button size="tiny" negative onClick={() => setConfirming(r)}>
                        Delete
                      </Button>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}

        <p style={{ color: '#767676' }}>
          Nobody reaches an organisation without being put there deliberately — that is why these
          accounts exist rather than the signup being refused, which would reveal which codes are
          real. The code they tried is recorded from 8 October 2026 onwards; accounts older than
          that never had it stored, so most of that column is empty.
        </p>

        {/* Deletion is irreversible, so it is confirmed rather than being one click in a table
            row next to a dropdown. */}
        <Modal size="small" open={confirming != null} onClose={() => setConfirming(null)}>
          <Modal.Header>Delete this account?</Modal.Header>
          <Modal.Content>
            <p>
              <strong>{confirming?.email ?? confirming?.name ?? `Account ${confirming?.userId}`}</strong>
              {' '}signed up on {confirming && formatDate(confirming.signedUpAt)} and belongs to no
              organisation.
            </p>
            <p>
              Their details are erased and the login stops working. This cannot be undone — the
              address is released, so they could sign up again from scratch. Use it for signups you
              do not recognise; somebody you are unsure about can simply be left waiting.
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              negative
              loading={removing}
              onClick={async () => {
                const target = confirming;
                setConfirming(null);
                await act(
                  () => remove({ variables: { userId: target.userId } }),
                  'Could not delete that account.',
                );
              }}
            >
              Delete the account
            </Button>
          </Modal.Actions>
        </Modal>
      </Container>
    </ResponsiveContainer>
  );
};
